import { Modal } from "react-bootstrap";
import { Button } from "./Button";
import { InfoCircle, Receipt, CreditCard2Back } from "react-bootstrap-icons";

export type CommitChangesConfirmDialogProps = {
  show: boolean;
  onClose: () => void;
  onConfirm: () => void;
  /** True when plan is billed by invoice (accounts receivable), false when auto-pay */
  isInvoiced: boolean;
};

export function CommitChangesConfirmDialog({
  show,
  onClose,
  onConfirm,
  isInvoiced,
}: CommitChangesConfirmDialogProps) {
  if (!show) return null;

  return (
    <Modal show={show} onHide={onClose} centered size="lg">
      <Modal.Header closeButton className="border-0 pb-0">
        <Modal.Title className="d-flex align-items-center gap-2">
          <InfoCircle className="text-primary" />
          Confirm save changes
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="pt-2">
        {isInvoiced ? <InvoicedContent /> : <AutoPayContent />}
      </Modal.Body>

      <Modal.Footer className="border-0 pt-0 d-flex justify-content-end gap-2">
        <Button onClick={onClose}>
          Cancel
        </Button>
        <Button className="btn-main" onClick={onConfirm}>
          Confirm & save
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

function InvoicedContent() {
  return (
    <div className="d-flex flex-column gap-4">
      <div className="bg-primary-subtle rounded p-3">
        <div className="d-flex align-items-center gap-2 mb-2">
          <Receipt className="text-primary fs-5" />
          <span className="fw-semibold">You will be invoiced for these changes</span>
        </div>
        <p className="mb-0 text-muted small">
          Please acknowledge how your changes will be billed:
        </p>
      </div>

      <div className="bg-warning-subtle rounded p-3">
        <div className="fw-semibold mb-1">Before payment &amp; period start</div>
        <p className="mb-0 small text-muted">
          If payment has not been made and the billing period has not yet started, you will receive an amended invoice reflecting the updated charges.
        </p>
      </div>

      <div className="bg-success-subtle rounded p-3">
        <div className="fw-semibold mb-1">After payment or period started</div>
        <p className="mb-0 small text-muted">
          If you have already paid or the period has started, the changes will be reflected on your next invoice.
        </p>
      </div>

      <div className="bg-light rounded p-3">
        <div className="fw-semibold mb-1">Summary</div>
        <ul className="mb-0 small text-muted ps-3">
          <li>Not yet paid / period not started ? amended invoice</li>
          <li>Already paid or period started ? next invoice</li>
        </ul>
      </div>
    </div>
  );
}

function AutoPayContent() {
  return (
    <div className="d-flex flex-column gap-4">
      <div className="bg-primary-subtle rounded p-3">
        <div className="d-flex align-items-center gap-2 mb-2">
          <CreditCard2Back className="text-primary fs-5" />
          <span className="fw-semibold">Changes will apply at next renewal</span>
        </div>
        <p className="mb-0 text-muted small">
          Your payment method is charged automatically at each renewal.
        </p>
      </div>

      <div className="bg-primary-subtle rounded p-3">
        <div className="fw-semibold mb-1">When your changes take effect</div>
        <p className="mb-0 small text-muted">
          The changes you are saving will be reflected retroactively in your next renewal cycle. Your current period charge is already set; your next automatic charge will include these updates.
        </p>
      </div>

      <div className="bg-light rounded p-3">
        <div className="fw-semibold mb-1">Summary</div>
        <ul className="mb-0 small text-muted ps-3">
          <li>Changes apply at your next renewal</li>
          <li>Current period is unchanged</li>
        </ul>
      </div>
    </div>
  );
}
